import React, { useState, useEffect, useRef } from "react";

/**
 * MultiLLMSupport component showcasing Raidu's multi-LLM provider capabilities
 * Displays an interactive demo of how Raidu can route requests to different LLM providers
 * 
 * @returns {JSX.Element} The rendered MultiLLMSupport component
 */
const MultiLLMSupport: React.FC = () => {
  try {
    // Log component rendering
    console.log("Rendering MultiLLMSupport component");

    // Define response type for type safety
    type ProviderResponses = {
      openai: string;
      anthropic: string;
      gemini: string;
      mistral: string;
      cohere: string;
    };
    
    // Define message type for type safety
    type ChatMessage = {
      type: 'user' | 'ai';
      text: string;
      responses?: ProviderResponses;
    };
    
    // State for chat messages and animation
    const [messages, setMessages] = useState<ChatMessage[]>([]); 
    const [isTyping, setIsTyping] = useState(false);
    const [currentMessage, setCurrentMessage] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [demoStarted, setDemoStarted] = useState(false);
    // selectedProvider is used in simulateProviderSelection function
    const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
    const [activeResponseTab, setActiveResponseTab] = useState<string>("openai");
    const [animationTimers, setAnimationTimers] = useState<NodeJS.Timeout[]>([]);
    
    // Reference to chat container for auto-scrolling
    const chatContainerRef = useRef<HTMLDivElement>(null);
    
    // AI provider information
    const aiProviders = [
      { 
        id: "openai", 
        name: "OpenAI", 
        icon: "/ai-icons/openai.svg",
        strengths: ["General knowledge", "Code generation", "Creative writing"],
        description: "Excellent for general tasks and creative content generation."
      },
      { 
        id: "anthropic", 
        name: "Anthropic", 
        icon: "/ai-icons/anthropic.svg",
        strengths: ["Safety", "Reasoning", "Long context"],
        description: "Strong reasoning capabilities with excellent safety features."
      },
      { 
        id: "gemini", 
        name: "Google Gemini", 
        icon: "/ai-icons/gemini-color.svg",
        strengths: ["Multimodal", "Real-time data", "Web search"],
        description: "Powerful multimodal capabilities with access to real-time information."
      },
      { 
        id: "mistral", 
        name: "Mistral AI", 
        icon: "/ai-icons/mistral-color.svg",
        strengths: ["Efficiency", "Technical tasks", "Multilingual"],
        description: "Highly efficient models with strong performance on technical tasks."
      },
      { 
        id: "cohere", 
        name: "Cohere", 
        icon: "/ai-icons/cohere-color.svg",
        strengths: ["Enterprise focus", "RAG", "Embeddings"],
        description: "Specialized in enterprise applications and retrieval tasks."
      }
    ];
    
    // Current provider index for rotation
    const [currentProviderIndex, setCurrentProviderIndex] = useState(0);
    
    // Define sample prompt type for type safety
    type SamplePrompt = {
      text: string;
      bestProvider: keyof ProviderResponses;
      responses: ProviderResponses;
    };
    
    // Sample prompt for demonstration with multiple provider responses
    const samplePrompts: SamplePrompt[] = [
      { 
        text: "Explain quantum computing in simple terms", 
        bestProvider: "openai",
        responses: {
          openai: "Quantum computing uses quantum bits or 'qubits' that can exist in multiple states simultaneously, unlike classical bits that are either 0 or 1. This property, called superposition, along with entanglement, allows quantum computers to process certain problems exponentially faster than classical computers. Think of it like checking many solutions at once rather than one at a time.",
          anthropic: "Imagine regular computers as using light switches that are either on or off. Quantum computers use special switches that can be on, off, or somewhere in between—all at the same time! This lets them solve certain problems much faster by exploring many possible answers simultaneously instead of checking one at a time.",
          gemini: "Quantum computing harnesses the strange rules of quantum physics to process information. While classical computers use bits (0s and 1s), quantum computers use qubits that can represent multiple values simultaneously through 'superposition.' This, combined with 'entanglement' (where qubits become linked), allows quantum computers to tackle certain complex problems far more efficiently than classical computers.",
          mistral: "Traditional computers work with bits (0s and 1s). Quantum computers use quantum bits or 'qubits' that can exist in multiple states at once thanks to quantum mechanics. This allows them to perform many calculations simultaneously, making them potentially much faster for specific problems like factoring large numbers or simulating quantum systems.",
          cohere: "Quantum computing leverages quantum mechanical phenomena to perform calculations. Unlike classical bits that must be either 0 or 1, quantum bits (qubits) can exist in superpositions of states. This, along with entanglement, allows quantum computers to process certain types of problems exponentially faster than classical computers."
        }
      }
    ];
    
    // Function to clear all animation timers
    const clearAllTimers = () => {
      try {
        // Clear all stored timers
        animationTimers.forEach(timer => clearTimeout(timer));
        setAnimationTimers([]);
      } catch (error) {
        console.error("Error clearing animation timers:", error);
      }
    };
    
    // Function to auto-scroll chat to bottom
    const scrollToBottom = () => {
      try {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      } catch (error) {
        console.error("Error scrolling chat to bottom:", error);
      }
    };
    
    // Effect to scroll chat to bottom when messages change
    useEffect(() => {
      scrollToBottom();
    }, [messages, currentMessage]);
    
    // Function to simulate typing animation 
    // Currently not used in the tabbed response version but kept for potential future use
    // This is intentionally kept for future enhancements
    const simulateTyping = (text: string, callback?: () => void) => {
      try {
        setIsTyping(true);
        setCurrentMessage("");
        
        let index = 0;
        const typingSpeed = 30; // ms per character
        
        const typingInterval = setInterval(() => {
          if (index < text.length) {
            setCurrentMessage(prev => prev + text.charAt(index));
            index++;
          } else {
            clearInterval(typingInterval);
            setIsTyping(false);
            setCurrentMessage("");
            if (callback) callback();
          }
        }, typingSpeed);
        
        // Store the interval for cleanup
        setAnimationTimers(prev => [...prev, typingInterval]);
        
        return typingInterval;
      } catch (error) {
        console.error("Error simulating typing:", error);
        setIsTyping(false);
        return null;
      }
    };
    
    // Function to simulate LLM provider selection
    const simulateProviderSelection = (providerId: string, callback?: () => void) => {
      try {
        // Find provider index
        const providerIndex = aiProviders.findIndex(p => p.id === providerId);
        if (providerIndex === -1) return;
        
        // Animate through providers
        let currentIndex = 0;
        const selectionInterval = setInterval(() => {
          setSelectedProvider(aiProviders[currentIndex].id);
          
          if (currentIndex === providerIndex) {
            clearInterval(selectionInterval);
            
            // Wait a bit before callback
            const callbackTimer = setTimeout(() => {
              if (callback) callback();
            }, 800);
            
            setAnimationTimers(prev => [...prev, callbackTimer]);
          } else {
            currentIndex = (currentIndex + 1) % aiProviders.length;
          }
        }, 300);
        
        setAnimationTimers(prev => [...prev, selectionInterval]);
      } catch (error) {
        console.error("Error simulating provider selection:", error);
      }
    };
    
    // Function to handle sending a message
    const handleSendMessage = (message: string = "") => {
      try {
        if (demoStarted) return;
        
        const userMessage = message || inputValue;
        if (!userMessage.trim()) return;
        
        setDemoStarted(true);
        setInputValue("");
        
        // Add user message to chat
        setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
        
        // Find a matching sample prompt or use the first one as default
        const promptMatch = samplePrompts.find(p => 
          p.text.toLowerCase() === userMessage.toLowerCase() ||
          p.text.toLowerCase().includes(userMessage.toLowerCase()) ||
          userMessage.toLowerCase().includes(p.text.toLowerCase())
        ) || samplePrompts[0];
        
        // Set the initial active tab to the best provider
        setActiveResponseTab(promptMatch.bestProvider);
        
        // Simulate provider selection
        simulateProviderSelection(promptMatch.bestProvider, () => {
          // After provider selection, add AI responses to chat
          setMessages(prev => [...prev, { 
            type: 'ai', 
            text: promptMatch.responses[promptMatch.bestProvider as keyof ProviderResponses],
            responses: promptMatch.responses
          }]);
          
          // Reset for next interaction after a delay
          setTimeout(() => {
            setDemoStarted(false);
            setSelectedProvider(null);
          }, 1000);
        });
        
        // Log the interaction
        console.log("User message:", userMessage);
      } catch (error) {
        console.error("Error handling send message:", error);
        setDemoStarted(false);
      }
    };

    // Function to reset the demo
    const resetDemo = () => {
      try {
        // Log reset action
        console.log("Resetting MultiLLMSupport demo");
        
        // Clear all animation timers
        clearAllTimers();
        
        // Reset all state
        setMessages([]);
        setIsTyping(false);
        setCurrentMessage("");
        setInputValue("");
        setDemoStarted(false);
        setSelectedProvider(null);
        setActiveResponseTab("openai");
        
        // Start demo with a suggested question after a short delay
        const startDemoTimer = setTimeout(() => {
          handleSendMessage(samplePrompts[0].text);
        }, 1000);
        
        setAnimationTimers([startDemoTimer]);
      } catch (error) {
        console.error("Error resetting demo:", error);
      }
    };
    
    // Set up icon rotation and start demo on component mount
    useEffect(() => {
      try {
        // Set up provider rotation for the reset button
        const providerRotationTimer = setInterval(() => {
          try {
            setCurrentProviderIndex(prevIndex => (prevIndex + 1) % aiProviders.length);
          } catch (error) {
            console.error("Error rotating AI provider icon:", error);
          }
        }, 3000); // Rotate every 3 seconds
        
        // Only start the demo if there are no messages yet
        // This prevents duplicate messages when component re-renders
        let startDemoTimer: NodeJS.Timeout | null = null;
        if (messages.length === 0) {
          startDemoTimer = setTimeout(() => {
            handleSendMessage(samplePrompts[0].text);
          }, 1000);
          
          if (startDemoTimer) {
            setAnimationTimers(prev => [...prev, startDemoTimer as NodeJS.Timeout]);
          }
        }
        
        setAnimationTimers(prev => [...prev, providerRotationTimer]);
        
        return () => {
          // Clean up all timers when component unmounts
          clearAllTimers();
          clearInterval(providerRotationTimer);
          if (startDemoTimer) clearTimeout(startDemoTimer);
        };
      } catch (error) {
        console.error("Error setting up MultiLLMSupport demo:", error);
      }
    }, [messages.length]);

    return (
      <div id="multi-llm-support" className="relative overflow-hidden">
        {/* Decorative elements matching Hero section */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-indigo-200 blur-3xl"></div>
          <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-purple-200 blur-3xl"></div>
          <div className="absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-pink-200 blur-3xl"></div>
        </div>
        
        {/* Subtle dot pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10" 
          style={{
            backgroundImage: 'radial-gradient(#4f46e5 0.5px, transparent 0.5px)',
            backgroundSize: '12px 12px'
          }}
        ></div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-purple-200/30 to-blue-200/30 rounded-full blur-3xl -z-10 transform translate-x-1/2 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-br from-indigo-200/30 to-purple-200/30 rounded-full blur-3xl -z-10 transform -translate-x-1/2 translate-y-1/4"></div>
        
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2 p-8 sm:p-12 lg:p-16">
            {/* Interactive Multi-LLM Demo - Now on the left */}
            <div className="relative mx-auto w-full max-w-xl lg:mx-0 lg:max-w-none order-2 lg:order-1">
              <div className="rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                {/* Demo header */}
                <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                    <div className="flex items-center ml-2">
                      <img src="/logos/raidu_logo.png" alt="Raidu" className="h-5 w-5 mr-2" />
                      <span className="text-gray-800 text-sm font-medium">Raidu Multi-LLM Router</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div 
                      onClick={resetDemo}
                      className="relative flex cursor-pointer transition-transform hover:scale-110"
                      title={`${aiProviders[currentProviderIndex].name} (Reset Demo)`}
                    >
                      {/* Rotating AI provider icon */}
                      <img 
                        src={aiProviders[currentProviderIndex].icon} 
                        alt={aiProviders[currentProviderIndex].name} 
                        className="h-7 w-7 z-10 transition-opacity duration-300" 
                        key={aiProviders[currentProviderIndex].name}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Demo content area */}
                <div className="flex flex-col h-[500px]">
                  {/* Chat interface */}
                  <div className="flex-1 flex flex-col">

                    {/* Chat messages */}
                    <div 
                      ref={chatContainerRef}
                      className="flex-1 overflow-y-auto p-4 bg-white" 
                      id="multi-llm-chat-container"
                    >
                      {messages.map((message, index) => (
                        <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
                          {message.type !== 'user' && (
                            <div className="flex-shrink-0 mr-2">
                              <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                AI
                              </div>
                            </div>
                          )}
                          <div 
                            className={`${message.type === 'user' 
                              ? 'bg-indigo-600 text-white max-w-[70%] rounded-lg px-4 py-2' 
                              : message.responses 
                                ? 'bg-white border border-gray-200 shadow-sm max-w-[85%] rounded-lg overflow-hidden' 
                                : 'bg-gray-100 text-gray-800 max-w-[70%] rounded-lg px-4 py-2'}`}
                            style={{ wordBreak: 'break-word' }}
                          >
                            {message.type === 'user' ? (
                              <p className="text-sm">{message.text}</p>
                            ) : message.responses ? (
                              <div className="w-full">
                                {/* Tabs for different AI providers */}
                                <div className="flex border-b border-gray-200 bg-gray-50">
                                  {message.responses && Object.keys(message.responses).map(providerId => {
                                    const provider = aiProviders.find(p => p.id === providerId);
                                    return (
                                      <button
                                        key={providerId}
                                        className={`flex items-center py-2 px-3 text-xs font-medium ${activeResponseTab === providerId
                                          ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                                        onClick={() => setActiveResponseTab(providerId)}
                                      >
                                        <img 
                                          src={provider?.icon} 
                                          alt={provider?.name || providerId} 
                                          className="h-5 w-5" 
                                          title={provider?.name || providerId}
                                        />
                                        {providerId === samplePrompts.find(p => 
                                          p.text.toLowerCase() === message.text.toLowerCase() || 
                                          p.text.toLowerCase().includes(message.text.toLowerCase()) || 
                                          message.text.toLowerCase().includes(p.text.toLowerCase())
                                        )?.bestProvider && (
                                          <span className="ml-1 text-[10px] text-green-600">★</span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                                
                                {/* Response content */}
                                <div className="p-4">
                                  <p className="text-sm whitespace-pre-wrap">{message.responses && message.responses[activeResponseTab as keyof ProviderResponses]}</p>
                                  <div className="mt-2 text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded inline-flex items-center">
                                    <img 
                                      src={aiProviders.find(p => p.id === activeResponseTab)?.icon} 
                                      alt={aiProviders.find(p => p.id === activeResponseTab)?.name || activeResponseTab} 
                                      className="h-3 w-3 mr-1" 
                                    />
                                    Powered by {aiProviders.find(p => p.id === activeResponseTab)?.name || activeResponseTab}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm">{message.text}</p>
                            )}
                          </div>
                          {message.type === 'user' && (
                            <div className="flex-shrink-0 ml-2">
                              <div className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold">
                                U
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Typing indicator */}
                      {isTyping && (
                        <div className="flex justify-start mb-4">
                          <div className="flex-shrink-0 mr-2">
                            <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                              AI
                            </div>
                          </div>
                          <div className="rounded-lg px-4 py-2 bg-gray-100 text-gray-800 max-w-[70%]">
                            <p className="text-sm">{currentMessage}<span className="inline-block w-2 h-4 bg-gray-400 ml-1 animate-pulse"></span></p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Chat input */}
                    <div className="border-t border-gray-200 p-3 bg-white">
                      <form 
                        className="relative" 
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (!demoStarted && inputValue.trim()) {
                            handleSendMessage();
                          }
                        }}
                      >
                        <input 
                          type="text" 
                          className="w-full rounded-full border border-gray-300 px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-ellipsis overflow-hidden"
                          placeholder="Enter your prompt..."
                          value={inputValue}
                          onChange={(e) => {
                            if (!demoStarted) {
                              setInputValue(e.target.value);
                            }
                          }}
                          disabled={demoStarted}
                        />
                        <button 
                          type="submit"
                          className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full ${demoStarted ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:bg-indigo-50 cursor-pointer'}`}
                          disabled={demoStarted}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Text section - Now on the right */}
            <div className="mx-auto w-full max-w-xl lg:mx-0 lg:ml-auto text-right order-1 lg:order-2">
              <div className="inline-block mb-4">
                <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent text-sm font-medium px-3 py-1 rounded-full bg-opacity-10 border border-indigo-200 bg-indigo-50">
                  Model Flexibility
                </span>
              </div>
              <h2 className="text-4xl font-semibold tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent sm:text-5xl">
                Multi-LLM Support
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Raidu seamlessly integrates with multiple AI providers, intelligently routing requests to the best model for each task. This ensures optimal performance, cost efficiency, and redundancy while giving you full control over which models handle your data.
              </p>
              <div className="mt-8 flex items-center gap-x-6 justify-end">
                <a
                  href="/booking"
                  className="rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => {
                    try {
                      // Log button click
                      console.log("MultiLLMSupport - Get Started button clicked");
                    } catch (error) {
                      // Log any errors during click handling
                      console.error(
                        "Error handling Get Started button click:",
                        error
                      );
                    }
                  }}
                >
                  Get Started
                </a>
                <a
                  href="/booking"
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 transition-colors"
                  onClick={() => {
                    try {
                      // Log button click
                      console.log("MultiLLMSupport - Learn More button clicked");
                    } catch (error) {
                      // Log any errors during click handling
                      console.error(
                        "Error handling Learn More button click:",
                        error
                      );
                    }
                  }}
                >
                  Learn More <span aria-hidden="true">&rarr;</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error("Error rendering MultiLLMSupport component:", error);

    // Return error state
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-base font-semibold text-indigo-600">Error</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Failed to load multi-LLM support section
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Please refresh the page or try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }
};

export default MultiLLMSupport;
